// src/components/Bookings/MyBookings.jsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchUserBookings } from '../../redux/slices/Bookings/bookingsSlice';
import { Helmet } from 'react-helmet-async';
import { formatMXDate } from '../../utils/dates';

const MyBookings = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { bookings, isLoading, error } = useSelector((state) => state.bookings);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserBookings());
    }
  }, [dispatch, user]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-[#EFE6DD]">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#9A0002] border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#EFE6DD] min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-14">
          <div className="bg-[#9A0002]/10 border border-[#9A0002]/30 text-[#9A0002] px-4 py-3 rounded-xl">
            {t('myBookings.error')}: {error}
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return formatMXDate(dateString, undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusKey = (status) => {
    const statusMap = {
      pending: 'pending',
      confirmed: 'confirmed',
      'checked-in': 'checkedIn',
      'checked-out': 'checkedOut',
      completed: 'completed',
      cancelled: 'cancelled',
    };
    return statusMap[status] || status;
  };

  // Tonos apagados que combinan con la paleta de marca en vez de los
  // colores saturados por defecto de Tailwind.
  const statusColors = {
    pending: 'bg-[#B08D57]/15 text-[#8A6A3A]',
    confirmed: 'bg-[#3A6B4C]/15 text-[#3A6B4C]',
    'checked-in': 'bg-[#3A5A7A]/15 text-[#3A5A7A]',
    'checked-out': 'bg-[#3A2A20]/10 text-[#3A2A20]/60',
    completed: 'bg-[#6B4A7A]/15 text-[#6B4A7A]',
    cancelled: 'bg-[#9A0002]/10 text-[#9A0002]',
  };

  if (!bookings.length) {
    return (
      <div className="bg-[#EFE6DD] min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <h2 className="font-['Fraunces'] text-2xl font-semibold text-[#3A2A20] mb-3">
            {t('myBookings.noBookings')}
          </h2>
          <p className="text-[#3A2A20]/60">
            {t('myBookings.noBookingsMessage')}{' '}
            <Link to="/rooms" className="text-[#9A0002] underline underline-offset-2 hover:text-[#7A0001]">
              {t('myBookings.bookNow')}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('myBookings.title')}</title>
      </Helmet>

      <div className="bg-[#EFE6DD] min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          {/* Header */}
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A0002]">
              {t('myBookings.eyebrow') || t('myBookings.title')}
            </span>
            <h1 className="mt-2 font-['Fraunces'] text-4xl md:text-5xl font-semibold text-[#3A2A20]">
              {t('myBookings.title')}
            </h1>
            <div className="mt-4 h-px w-24 border-t-2 border-dashed border-[#9A0002]/30" />
          </div>

          <div className="space-y-6">
            {bookings.map((booking) => (
              <div
                key={booking._id}
                className="bg-white rounded-2xl border border-[#3A2A20]/10 hover:border-[#9A0002]/30 hover:shadow-md transition overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div>
                      <h3 className="font-['Fraunces'] text-xl font-semibold text-[#3A2A20]">
                        {booking.room?.name || t('myBookings.roomPlaceholder')}
                        <span className="text-sm font-sans font-normal text-[#3A2A20]/50 ml-2">
                          #{booking.room?.roomNumber}
                        </span>
                      </h3>
                      <p className="text-[#3A2A20]/60 mt-1 text-sm">
                        {formatDate(booking.checkInDate)} → {formatDate(booking.checkOutDate)}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                        statusColors[booking.status] || 'bg-[#3A2A20]/10 text-[#3A2A20]/60'
                      }`}
                    >
                      {t(`myBookings.${getStatusKey(booking.status)}`)}
                    </span>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                        {t('myBookings.dogs')}
                      </p>
                      <p className="text-sm font-medium text-[#3A2A20] mt-1">
                        {booking.dogs?.length > 0
                          ? booking.dogs.map(dog => dog.name).join(', ')
                          : t('myBookings.noDogsSpecified')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                        {t('myBookings.totalPrice')}
                      </p>
                      <p className="font-['Fraunces'] text-lg font-semibold text-[#9A0002] mt-0.5">
                        ${booking.totalPrice}
                      </p>
                    </div>
                    {booking.specialRequests && (
                      <div className="sm:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                          {t('myBookings.specialRequests')}
                        </p>
                        <p className="italic text-[#3A2A20]/70 text-sm mt-1">{booking.specialRequests}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 pt-4 border-t border-[#3A2A20]/10 text-xs text-[#3A2A20]/40">
                    {t('myBookings.bookedOn')} {formatDate(booking.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyBookings;